'use client';
import { useState, useEffect, createContext, useContext, ReactNode, useCallback } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useAuth } from './AuthContext';
import { useApi } from './ApiContext';

/**
 * Simplified show data for context
 * Only includes fields actually needed by the UI
 */
export interface ShowContextShow {
  id: string;
  name: string;
  slug: string;
  description?: string;
  activeEpisodeId?: string;
  compactViewVoiceOnly?: boolean;
}

interface ShowContextType {
  currentShow: ShowContextShow | null;
  availableShows: ShowContextShow[];
  loading: boolean;
  setCurrentShow: (show: ShowContextShow) => void;
  setCurrentShowById: (showId: string) => void;
  setCurrentShowBySlug: (slug: string) => void;
  hasMultipleShows: boolean;
}

const ShowContext = createContext<ShowContextType | undefined>(undefined);

interface ShowProviderProps {
  children: ReactNode;
}

const SHOW_STORAGE_KEY = 'gs_focused_show';

const getStoredShowRaw = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage.getItem(SHOW_STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to read stored show from localStorage:', error);
    return null;
  }
};

const parseStoredShowSnapshot = (rawValue: string | null): ShowContextShow | null => {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.id === 'string' &&
      typeof parsed.slug === 'string' &&
      typeof parsed.name === 'string'
    ) {
      return parsed as ShowContextShow;
    }
  } catch {
    // Legacy format (slug/id string) - ignore for snapshot hydration
  }

  return null;
};

const deriveStoredIdentifiers = (rawValue: string | null): { slug?: string; id?: string } => {
  if (!rawValue) {
    return {};
  }

  try {
    const parsed = JSON.parse(rawValue);
    return {
      slug: typeof parsed.slug === 'string' ? parsed.slug : undefined,
      id: typeof parsed.id === 'string' ? parsed.id : undefined,
    };
  } catch {
    // Previous storage format only stored the slug/id string
    return { slug: rawValue, id: rawValue };
  }
};

export const ShowProvider = ({ children }: ShowProviderProps) => {
  const { user, loading: authLoading } = useAuth();
  const apiClient = useApi();
  const [currentShow, setCurrentShowState] = useState<ShowContextShow | null>(() => {
    return parseStoredShowSnapshot(getStoredShowRaw());
  });
  const [availableShows, setAvailableShows] = useState<ShowContextShow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  // Persist current show to localStorage
  const setCurrentShow = useCallback((show: ShowContextShow) => {
    setCurrentShowState(show);

    if (typeof window !== 'undefined') {
      window.localStorage.setItem(SHOW_STORAGE_KEY, JSON.stringify(show));
    }

    datadogRum.setGlobalContextProperty('showName', show.name);
  }, []);

  // Load available shows based on user's ACL
  useEffect(() => {
    if (authLoading) {
      return;
    }

    const loadAvailableShows = async () => {
      if (!user || !user.acl || user.acl.length === 0) {
        setAvailableShows([]);
        setCurrentShowState(null);
        setLoading(false);
        return;
      }

      try {
        // Fetch shows available to the user via the standard API
        const response = await apiClient.standard.getAvailableShows();
        const userShows = response.shows;

        setAvailableShows(userShows);

        // Try to restore previously selected show from localStorage
        const storedRaw = getStoredShowRaw();
        const { slug: storedSlug, id: storedId } = deriveStoredIdentifiers(storedRaw);

        const restoredShow =
          (storedSlug && userShows.find(s => s.slug === storedSlug)) ||
          (storedId && userShows.find(s => s.id === storedId));

        if (restoredShow) {
          setCurrentShow(restoredShow);
          setLoading(false);
          return;
        }

        // If no stored show or it's invalid, default to first available show
        if (userShows.length > 0) {
          setCurrentShow(userShows[0]);
        }
      } catch (error) {
        console.error('Failed to load available shows:', error);
        setAvailableShows([]);
      } finally {
        setLoading(false);
      }
    };

    loadAvailableShows();
  }, [user, apiClient, setCurrentShow, authLoading]);

  // Set current show by ID
  const setCurrentShowById = useCallback((showId: string) => {
    const show = availableShows.find(s => s.id === showId);
    if (show) {
      setCurrentShow(show);
    } else {
      console.warn(`Show with ID ${showId} not found in available shows`);
    }
  }, [availableShows, setCurrentShow]);

  // Set current show by slug
  const setCurrentShowBySlug = useCallback((slug: string) => {
    const show = availableShows.find(s => s.slug === slug);
    if (show) {
      setCurrentShow(show);
    } else {
      console.warn(`Show with slug ${slug} not found in available shows`);
    }
  }, [availableShows, setCurrentShow]);

  const hasMultipleShows = availableShows.length > 1;

  return (
    <ShowContext.Provider value={{
      currentShow,
      availableShows,
      loading,
      setCurrentShow,
      setCurrentShowById,
      setCurrentShowBySlug,
      hasMultipleShows,
    }}>
      {children}
    </ShowContext.Provider>
  );
};

export const useShow = (): ShowContextType => {
  const context = useContext(ShowContext);
  if (!context) {
    throw new Error('useShow must be used within a ShowProvider');
  }
  return context;
};
